import { ulid } from 'ulid';
import { dispatch } from '../events/dispatcher.js';
import { db, getOrCreateSettings } from '../db/db.js';
import { updateSettings } from './settings.js';
import type {
  Category,
  ExpirySource,
  Location,
} from '../events/types.js';
import { lookupKnowledge } from './knowledge.js';
import { normalizeName } from './normalize.js';

const DEFAULT_GENERIC: { category: Category; location: Location; expiryDays: number; unit: string } = {
  category: 'other',
  location: 'pantry',
  expiryDays: 30,
  unit: '個',
};

export interface QuickAddInput {
  name: string;
  /** Optional override; otherwise filled from knowledge / generic fallback. */
  category?: Category;
  location?: Location;
  quantity?: number;
  /** packaging unit（盒/瓶/罐...）；不填則從 PantryKnowledge.defaultUnit 推。 */
  unit?: string;
  expiresAt?: Date | null;
  expirySource?: ExpirySource;
  /** 包裝規格量（每盒裝幾顆 / 每瓶裝幾 ml）。 */
  packageSize?: number;
  /** 規格單位（'顆' / 'ml' / 'g'...）。 */
  packageContentUnit?: string;
  /** v0.2 常備需求量（base unit）— 同步寫進 Item.minQuantity。 */
  minQuantity?: number;
}

export interface QuickAddResult {
  itemId: string;
  batchId: string;
  isNewItem: boolean;
}

/**
 * Quick Add: create a new Item (or append a Batch to an existing one) from a
 * single name + optional overrides. Smart Defaults Hierarchy (SPEC §6.2):
 *   1. (v0.2+) user history
 *   2. Pantry Knowledge Base
 *   3. (v0.2+) category inference
 *   4. Generic fallback
 *
 * v0.1 only steps 2 + 4 are wired up. Steps 1 / 3 are added later.
 */
export async function quickAdd(input: QuickAddInput): Promise<QuickAddResult> {
  const trimmedName = input.name.trim();
  if (!trimmedName) {
    throw new Error('quickAdd: name is required');
  }
  const normalized = normalizeName(trimmedName);
  const knowledge = await lookupKnowledge(trimmedName);

  const category = input.category ?? knowledge?.category ?? DEFAULT_GENERIC.category;
  const location = input.location ?? knowledge?.defaultLocation ?? DEFAULT_GENERIC.location;
  const packagingUnit = input.unit ?? knowledge?.defaultUnit ?? DEFAULT_GENERIC.unit;
  const buyQuantity = input.quantity ?? 1;
  const expiryDaysFallback = knowledge?.defaultExpiryDays ?? DEFAULT_GENERIC.expiryDays;
  const expiresAt =
    input.expiresAt !== undefined
      ? input.expiresAt?.getTime() ?? null
      : Date.now() + expiryDaysFallback * 86_400_000;
  const expirySource: ExpirySource =
    input.expirySource ??
    (input.expiresAt !== undefined
      ? 'manual'
      : knowledge
      ? 'knowledge_base'
      : 'estimated');

  // v0.3.4 重設計：batch 永遠用 packaging storage（quantity=瓶/盒/包數，unit=packaging）。
  // packageSize / packageUnit 為 metadata（每包裝多少 base unit），用於比價 + 拆 batch。
  const trimmedContentUnit = input.packageContentUnit?.trim() ?? '';
  const hasPackagingInfo =
    input.packageSize !== undefined &&
    input.packageSize > 0 &&
    trimmedContentUnit.length > 0 &&
    trimmedContentUnit !== packagingUnit;

  const existing = await db.items
    .where('normalizedName')
    .equals(normalized)
    .first();

  await rememberLocation(location);

  let resultItemId: string;
  let resultBatchId: string;
  let resultIsNewItem: boolean;

  // 對既有同名 item：active 直接加 batch；all_consumed / all_expired 加 batch 並復活
  // （projector applyBatchAdded 已負責 status 復活）。只有 discarded 才走「建新 item」分支。
  if (existing && existing.status !== 'discarded') {
    const batchId = ulid();
    await dispatch('batch_added', {
      itemId: existing.id,
      batchId,
      quantity: buyQuantity,
      unit: packagingUnit,
      ...(hasPackagingInfo
        ? { packageSize: input.packageSize, packageUnit: trimmedContentUnit }
        : {}),
      addedAt: Date.now(),
      expiresAt,
      expirySource,
    });
    resultItemId = existing.id;
    resultBatchId = batchId;
    resultIsNewItem = existing.status !== 'active';
  } else {
    const itemId = ulid();
    const batchId = ulid();
    await dispatch('item_created', {
      itemId,
      name: trimmedName,
      normalizedName: normalized,
      category,
      location,
      initialBatch: {
        itemId,
        batchId,
        quantity: buyQuantity,
        unit: packagingUnit,
        ...(hasPackagingInfo
          ? { packageSize: input.packageSize, packageUnit: trimmedContentUnit }
          : {}),
        addedAt: Date.now(),
        expiresAt,
        expirySource,
      },
    });
    resultItemId = itemId;
    resultBatchId = batchId;
    resultIsNewItem = true;
  }

  // 同時帶常備需求量（v0.2）
  if (input.minQuantity !== undefined && input.minQuantity > 0) {
    await dispatch('item_min_quantity_set', {
      itemId: resultItemId,
      minQuantity: Math.floor(input.minQuantity),
    });
  }

  return { itemId: resultItemId, batchId: resultBatchId, isNewItem: resultIsNewItem };
}

/**
 * Consume FIFO across an item's batches: drains the earliest-expiring batch first.
 * Batches with null expiresAt are consumed last.
 *
 * Returns the actually consumed amount (may be < requested if stock runs out).
 */
export async function consumeFifo(itemId: string, amount: number): Promise<number> {
  if (amount <= 0) return 0;
  const item = await db.items.get(itemId);
  if (!item) return 0;

  const sortedBatches = [...item.batches]
    .filter((b) => b.quantity > 0)
    .sort(sortByExpiry);
  let remaining = amount;
  let consumed = 0;
  for (const batch of sortedBatches) {
    if (remaining <= 0) break;
    const take = Math.min(batch.quantity, remaining);
    await dispatch('batch_consumed', {
      itemId,
      batchId: batch.id,
      amount: take,
    });
    remaining -= take;
    consumed += take;
  }
  return consumed;
}

function sortByExpiry(a: { expiresAt: number | null }, b: { expiresAt: number | null }): number {
  if (a.expiresAt === null && b.expiresAt === null) return 0;
  if (a.expiresAt === null) return 1;
  if (b.expiresAt === null) return -1;
  return a.expiresAt - b.expiresAt;
}

/** Consume a specific batch by amount; caps at the batch's current quantity. */
export async function consumeBatchAmount(
  itemId: string,
  batchId: string,
  amount: number
): Promise<number> {
  if (amount <= 0) return 0;
  const item = await db.items.get(itemId);
  if (!item) return 0;
  const batch = item.batches.find((b) => b.id === batchId);
  if (!batch || batch.quantity <= 0) return 0;
  const take = Math.min(batch.quantity, amount);
  await dispatch('batch_consumed', { itemId, batchId, amount: take });
  return take;
}

/** Adjust a batch's quantity to an exact value (修改批次數量；非消耗)。 */
export async function setBatchQuantity(
  itemId: string,
  batchId: string,
  newQuantity: number,
  reason?: string,
): Promise<void> {
  const safe = Math.max(0, newQuantity);
  await dispatch('batch_quantity_adjusted', {
    itemId,
    batchId,
    newQuantity: safe,
    ...(reason ? { reason } : {}),
  });
}

/**
 * 通用批次 patch（v0.3）：一次更新 quantity / unit / packageSize / packageUnit / expiresAt。
 * 用於 Item Detail「修改批次」對話框 — 補上加入時沒填的規格 / 改到期日 / 切 base unit。
 *
 * 傳 packageSize: null 或 packageUnit: '' 可清掉該欄位（變回 packaging-unit batch）。
 */
export interface UpdateBatchPatch {
  quantity?: number;
  unit?: string;
  packageSize?: number | null;
  packageUnit?: string | null;
  expiresAt?: Date | null;
}

export async function updateBatch(
  itemId: string,
  batchId: string,
  patch: UpdateBatchPatch,
): Promise<void> {
  const payload: Record<string, unknown> = { itemId, batchId };
  if (patch.quantity !== undefined) payload.quantity = Math.max(0, patch.quantity);
  if (patch.unit !== undefined) payload.unit = patch.unit;
  if (patch.packageSize !== undefined) payload.packageSize = patch.packageSize;
  if (patch.packageUnit !== undefined) payload.packageUnit = patch.packageUnit;
  if (patch.expiresAt !== undefined) {
    payload.expiresAt = patch.expiresAt === null ? null : patch.expiresAt.getTime();
    payload.expirySource = 'manual';
  }
  await dispatch('batch_updated', payload as never);
}

/**
 * 標記使用中（v0.3 重寫；v0.5.1 起改名，原「標記開瓶」過於液體導向）。
 *
 * 行為依批次當下狀態：
 * - 整批就一個 packaging（quantity == packageSize、或無 packageSize）→ 整批標 opened
 * - 多個 packaging 在同 batch（base-unit storage，例：4 瓶 = 3744ml）→ 拆 batch
 *   1. 新建 opened batch（packageSize 個 base unit）含 openedAt
 *   2. 原 batch.quantity 扣掉 packageSize（保留為未開封剩餘）
 *
 * 開瓶後到期日縮短：若 PantryKnowledge.openedExpiryDays 有值，新 batch 用該日期；
 * 否則沿用原 expiresAt。
 */
export async function markBatchOpened(
  itemId: string,
  batchId: string,
  openedAt: number = Date.now(),
): Promise<{ openedBatchId: string }> {
  const item = await db.items.get(itemId);
  if (!item) throw new Error('item not found');
  const batch = item.batches.find((b) => b.id === batchId);
  if (!batch) throw new Error('batch not found');
  if (batch.openedAt !== null) {
    return { openedBatchId: batch.id }; // already opened
  }

  // v0.3.4：batch 是 packaging storage（quantity = N 瓶）。
  // 拆條件：quantity > 1（有多個 packaging 才拆出 1 個獨立 opened）。
  const needSplit = batch.quantity > 1;

  if (!needSplit) {
    await dispatch('batch_opened', { itemId, batchId, openedAt });
    return { openedBatchId: batchId };
  }

  // Split：新建 opened batch（quantity=1）+ 原 batch 扣 1
  const newBatchId = ulid();
  const knowledge = await lookupKnowledge(item.name);
  const openedExpiry =
    knowledge?.openedExpiryDays !== undefined
      ? openedAt + knowledge.openedExpiryDays * 86_400_000
      : batch.expiresAt;

  await dispatch('batch_quantity_adjusted', {
    itemId,
    batchId,
    newQuantity: batch.quantity - 1,
    reason: 'split_for_opened',
  });
  await dispatch('batch_added', {
    itemId,
    batchId: newBatchId,
    quantity: 1,
    unit: batch.unit,
    ...(batch.packageSize !== undefined ? { packageSize: batch.packageSize } : {}),
    ...(batch.packageUnit !== undefined ? { packageUnit: batch.packageUnit } : {}),
    addedAt: batch.addedAt,
    expiresAt: openedExpiry,
    expirySource: knowledge?.openedExpiryDays !== undefined ? 'manual' : batch.expirySource,
  });
  await dispatch('batch_opened', {
    itemId,
    batchId: newBatchId,
    openedAt,
  });

  return { openedBatchId: newBatchId };
}

/** Discard a single batch (e.g. expired / spoiled). */
export async function discardBatch(
  itemId: string,
  batchId: string,
  reason?: 'expired' | 'spoiled' | 'other'
): Promise<void> {
  const payload: { itemId: string; batchId: string; reason?: 'expired' | 'spoiled' | 'other' } = {
    itemId,
    batchId,
  };
  if (reason) payload.reason = reason;
  await dispatch('batch_discarded', payload);
}

/** Hard-discard an item (all batches gone, hide from active inventory). */
export async function discardItem(itemId: string): Promise<void> {
  await dispatch('item_discarded', { itemId });
}

/**
 * 設定 / 取消常備需求量。傳 0 表示取消常備（從建議補貨清單移除）。
 * v0.2 採買主軸：只有 minQuantity > 0 的 item 才會出現在「建議補貨」。
 */
export async function setItemMinQuantity(itemId: string, minQuantity: number): Promise<void> {
  const safe = Math.max(0, Math.floor(minQuantity));
  await dispatch('item_min_quantity_set', { itemId, minQuantity: safe });
}

async function rememberLocation(location: Location): Promise<void> {
  const settings = await getOrCreateSettings();
  if (settings.knownLocations.includes(location)) return;
  await updateSettings({ knownLocations: [...settings.knownLocations, location] });
}
